import type { User, UserFullName } from 'api/models/mainservice';
import type { AppUser } from 'common/models';
import type { ContactPerson, PhoneNumber } from 'pages/customers/models';
import type { HumanResource } from 'pages/settings/users/models';
import type { AppUserDetail } from 'pages/user/settings/models';

import { removeDiacritics } from './validation';

interface CountryCode {
  isoCode: string;
  numberPrefix: string;
}

export interface BasicContactPerson {
  firstName: string;
  lastName: string;
  position?: string | null;
  phone?: PhoneNumber | null;
  email?: string | null;
}

export const SUPPORTED_PREFIX: CountryCode[] = [
  {
    isoCode: 'CZ',
    numberPrefix: '+420'
  },
  {
    isoCode: 'SK',
    numberPrefix: '+421'
  },
  {
    isoCode: 'AT',
    numberPrefix: '+43'
  },
  {
    isoCode: 'BE',
    numberPrefix: '+32'
  },
  {
    isoCode: 'BG',
    numberPrefix: '+359'
  },
  {
    isoCode: 'HR',
    numberPrefix: '+385'
  },
  {
    isoCode: 'CY',
    numberPrefix: '+357'
  },
  {
    isoCode: 'DK',
    numberPrefix: '+45'
  },
  {
    isoCode: 'EE',
    numberPrefix: '+372'
  },
  {
    isoCode: 'FI',
    numberPrefix: '+358'
  },
  {
    isoCode: 'FR',
    numberPrefix: '+33'
  },
  {
    isoCode: 'DE',
    numberPrefix: '+49'
  },
  {
    isoCode: 'GR',
    numberPrefix: '+30'
  },
  {
    isoCode: 'HU',
    numberPrefix: '+36'
  },
  {
    isoCode: 'IS',
    numberPrefix: '+354'
  },
  {
    isoCode: 'IE',
    numberPrefix: '+353'
  },
  {
    isoCode: 'IT',
    numberPrefix: '+39'
  },
  {
    isoCode: 'LV',
    numberPrefix: '+371'
  },
  {
    isoCode: 'LI',
    numberPrefix: '+423'
  },
  {
    isoCode: 'LT',
    numberPrefix: '+370'
  },
  {
    isoCode: 'LU',
    numberPrefix: '+352'
  },
  {
    isoCode: 'MT',
    numberPrefix: '+356'
  },
  {
    isoCode: 'NL',
    numberPrefix: '+31'
  },
  {
    isoCode: 'NO',
    numberPrefix: '+47'
  },
  {
    isoCode: 'PL',
    numberPrefix: '+48'
  },
  {
    isoCode: 'PT',
    numberPrefix: '+351'
  },
  {
    isoCode: 'RO',
    numberPrefix: '+40'
  },
  {
    isoCode: 'SI',
    numberPrefix: '+386'
  },
  {
    isoCode: 'ES',
    numberPrefix: '+34'
  },
  {
    isoCode: 'CH',
    numberPrefix: '+41'
  },
  {
    isoCode: 'GB',
    numberPrefix: '+44'
  },
  {
    isoCode: 'SE',
    numberPrefix: '+46'
  }
];

export const getFullName = (
  user: ContactPerson | User | AppUserDetail | AppUser | HumanResource | UserFullName
) => {
  return `${user.firstName} ${user.lastName}`;
};

export const getInitials = (user: { firstName: string; lastName: string }) => {
  const firstNameParts = user.firstName.trim().split(/\s+/);
  if (firstNameParts.length > 1) {
    // Dvouslovné jméno: vezmi první písmena obou slov
    return `${firstNameParts[0][0]}${firstNameParts[1][0]}${user.lastName[0]}`.toLocaleUpperCase();
  }
  // Jednoslovné jméno: první písmeno jména a příjmení
  return `${user.firstName[0]}${user.lastName[0]}`.toLocaleUpperCase();
};

export const getInitialPhoneNumberPrefix = (countryId?: string) => {
  if (countryId === 'SK') return '+421'; // SK

  return '+420'; // CZ
};

export const getPhoneNumber = (phone?: PhoneNumber | null) => {
  return phone ? `${phone.prefix}${phone.number}` : '';
};

export const getPhoneNumberLink = (phone?: PhoneNumber | null) => {
  return phone ? (
    <span onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer' }}>
      <a href={`tel:${phone.prefix}${phone.number}`}>{`${phone.prefix}${phone.number}`}</a>
    </span>
  ) : (
    ''
  );
};

export const getEmailLink = (email?: string | null) => {
  return email ? (
    <span onClick={(e) => e.stopPropagation()} style={{ cursor: 'pointer' }}>
      <a href={`mailto:${email}`}>{email}</a>
    </span>
  ) : (
    ''
  );
};

export const searchForExistingContactPerson = (
  data: BasicContactPerson,
  contactPersons: ContactPerson[]
) => {
  const strictMatch = contactPersons.filter(
    (person) =>
      removeDiacritics(person.firstName.toLowerCase()) ===
        removeDiacritics(data.firstName.toLowerCase()) &&
      removeDiacritics(person.lastName.toLowerCase()) ===
        removeDiacritics(data.lastName.toLowerCase()) &&
      person.email?.toLowerCase() === data.email?.toLowerCase() &&
      getPhoneNumber(person.phone) === getPhoneNumber(data.phone)
  );

  if (strictMatch.length > 1) {
    return undefined;
  } else if (strictMatch.length === 1) {
    return strictMatch[0];
  }

  const emailOrPhoneMatch = contactPersons.filter(
    (person) =>
      removeDiacritics(person.firstName.toLowerCase()) ===
        removeDiacritics(data.firstName.toLowerCase()) &&
      removeDiacritics(person.lastName.toLowerCase()) ===
        removeDiacritics(data.lastName.toLowerCase()) &&
      (person.email?.toLowerCase() === data.email?.toLowerCase() ||
        getPhoneNumber(person.phone) === getPhoneNumber(data.phone))
  );

  if (emailOrPhoneMatch.length > 1) {
    return undefined;
  } else if (emailOrPhoneMatch.length === 1) {
    return emailOrPhoneMatch[0];
  }

  const someMatch = contactPersons.filter(
    (person) =>
      (removeDiacritics(person.firstName.toLowerCase()) ===
        removeDiacritics(data.firstName.toLowerCase()) &&
        removeDiacritics(person.lastName.toLowerCase()) ===
          removeDiacritics(data.lastName.toLowerCase())) ||
      person.email?.toLowerCase() === data.email?.toLowerCase() ||
      getPhoneNumber(person.phone) === getPhoneNumber(data.phone)
  );

  if (someMatch.length > 1) {
    return undefined;
  } else if (someMatch.length === 1) {
    return someMatch[0];
  }

  return undefined;
};
